/**
 * Axios Instance met Offline Queue Interceptor
 * 
 * Automatisch alle API calls beschermen tegen netwerk/deploy onderbrekingen.
 * Werkt voor het hele systeem: werklijsten, instellingen, klanten, etc.
 */

import axios from 'axios';
import { offlineQueue } from './offlineQueue';
import { toast } from 'sonner';

// Maak axios instance
const api = axios.create({
  timeout: 15000, // 15 seconden timeout
});

// Request interceptor - voeg token toe
api.interceptors.request.use(
  (config) => {
    // Skip als er al een Authorization header is meegegeven (bijv. klant_token)
    if (config.headers?.Authorization) {
      return config;
    }
    
    // Anders, voeg de medewerker token toe als die bestaat
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 (ongeldig/verlopen token) en netwerk errors
api.interceptors.response.use(
  (response) => {
    // Als deze response uit de service-worker cache komt (offline modus),
    // markeer de data zodat UI kan tonen "je kijkt naar een offline versie".
    if (response.headers?.['x-waszbase-offline']) {
      response.data = response.data || {};
      if (typeof response.data === 'object' && !Array.isArray(response.data)) {
        response.data._offline = true;
        response.data._offline_age_ms = Number(response.headers['x-waszbase-cached-age-ms'] || 0);
      }
    }
    return response;
  },
  async (error) => {
    const config = error.config;

    // ============================================================
    // OFFLINE: service worker gaf een synthetic 503 terug omdat er
    // geen internet is én geen gecachte data. Toon nette melding.
    // ============================================================
    if (error.response?.status === 503 && error.response.headers?.['x-waszbase-offline']) {
      toast.error('Geen internet én geen gecachte data voor deze pagina. Probeer straks opnieuw.', { duration: 4000, id: 'offline-no-cache' });
      return Promise.reject(error);
    }

    // ============================================================
    // TIJDELIJKE 5xx / CLOUDFLARE FOUTEN → automatisch retry
    // ============================================================
    // Waszbase draait achter Cloudflare + Kubernetes. Tijdens een pod-restart
    // of tijdelijke hiccup krijg je een 502/503/504 of Cloudflare 520/521/524.
    // De gebruiker ziet dan "The origin web server sent a response that
    // Cloudflare could not parse..." — dat is bijna altijd voorbij binnen
    // 1-3 seconden. In plaats van de rauwe fout laten zien, proberen we
    // stilletjes 2× opnieuw.
    const status = error.response?.status;
    const isRetryableStatus = status === 502 || status === 503 || status === 504 ||
      status === 520 || status === 521 || status === 522 || status === 524;

    if (isRetryableStatus && config && !config._retryCount) {
      config._retryCount = 0;
    }
    if (isRetryableStatus && config && config._retryCount < 2) {
      config._retryCount += 1;
      const delayMs = config._retryCount === 1 ? 800 : 2000;
      await new Promise(res => setTimeout(res, delayMs));
      return api.request(config);
    }
    if (isRetryableStatus && config?._retryCount >= 2) {
      // Laatste poging faalde ook — vervang de rauwe Cloudflare-melding
      // door een nette Nederlandse tekst zodat de user niet in paniek raakt.
      toast.error('Waszbase is even niet bereikbaar. Probeer het over 10 seconden opnieuw.', { duration: 5000 });
    }
    
    // 401 Unauthorized - token verlopen of ongeldig
    // BELANGRIJK: alleen redirecten als de token zélf het probleem is. Bij een
    // 401 op één endpoint terwijl de rest van de session prima werkt (race
    // conditions, single bad endpoint), gewoon de fout teruggeven. Anders
    // krijgen we een loop waarbij een succesvolle login direct wordt
    // gevolgd door een logout omdat één van de parallelle calls 401 geeft.
    const isTokenInvalid = (rawToken) => {
      if (!rawToken) return true;
      try {
        const payload = JSON.parse(atob(rawToken.split('.')[1]));
        if (!payload.exp) return false;
        return Date.now() >= payload.exp * 1000;
      } catch {
        return true;
      }
    };

    if (error.response?.status === 401) {
      const isLoginRequest =
        config?.url?.includes('/auth/login') ||
        config?.url?.includes('/klant-auth/login') ||
        config?.url?.includes('/klant-portal/auth/login');
      // Tijdens admin-impersonation zit de admin OP /admin/klant-preview/ maar
      // fetcht klant-portal endpoints. Nooit redirecten in dat geval.
      const isAdminImpersonation = window.location.pathname.startsWith('/admin/klant-preview');
      if (!isLoginRequest && !isAdminImpersonation) {
        const klantToken = localStorage.getItem('klant_token');
        const medewerkerToken = localStorage.getItem('token');
        const heeftKlantToken = !!klantToken;
        const opKlantPad =
          window.location.pathname.startsWith('/klant') ||
          config?.url?.includes('/klant-portal/');

        // Alleen sessie wissen + redirecten als de TOKEN ZELF expired/invalid is.
        // Dit voorkomt redirect-loops bij random single-endpoint 401's.
        if (heeftKlantToken || opKlantPad) {
          if (isTokenInvalid(klantToken)) {
            localStorage.removeItem('klant_token');
            localStorage.removeItem('klant_user');
            localStorage.removeItem('klant_naam');
            if (!window.location.pathname.includes('/klant/login')) {
              window.location.href = '/klant/login';
            }
          }
        } else {
          if (isTokenInvalid(medewerkerToken)) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (
              !window.location.pathname.includes('/login') ||
              window.location.pathname.includes('/klant/login')
            ) {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    }
    
    // Skip als dit een GET request is (alleen mutaties queuen)
    if (config?.method?.toUpperCase() === 'GET') {
      return Promise.reject(error);
    }
    
    // Skip als dit al een retry is vanuit de queue
    if (config?._isRetry) {
      return Promise.reject(error);
    }
    
    // Check of het een netwerk error is
    const isNetworkError = !navigator.onLine || 
      error.code === 'ECONNABORTED' || 
      error.code === 'ERR_NETWORK' ||
      error.message === 'Network Error' ||
      (error.response === undefined && error.request);
    
    if (isNetworkError && config) {
      // Bepaal een beschrijving voor de actie
      const actionDescription = getActionDescription(config);
      
      // Voeg toe aan offline queue
      offlineQueue.addToQueue({
        type: 'api_call',
        endpoint: config.url,
        method: config.method?.toUpperCase() || 'POST',
        data: config.data instanceof FormData ? undefined : (typeof config.data === 'string' ? JSON.parse(config.data) : config.data),
        token: localStorage.getItem('token'),
        meta: {
          description: actionDescription,
          originalUrl: config.url,
          timestamp: new Date().toISOString()
        }
      });
      
      toast.info(`${actionDescription} - wordt gesynchroniseerd zodra verbinding terug is`, {
        duration: 3000
      });
      
      // Return een resolved promise zodat de UI niet crasht
      // De actie is opgeslagen en wordt later uitgevoerd
      return Promise.resolve({ 
        data: { queued: true, message: 'Actie opgeslagen voor synchronisatie' },
        status: 202,
        statusText: 'Accepted (Queued)',
        config
      });
    }
    
    return Promise.reject(error);
  }
);

// Helper functie om een beschrijving te maken van de actie
function getActionDescription(config) {
  const url = config.url || '';
  const method = config.method?.toUpperCase() || 'POST';
  
  // Parse de URL voor context
  if (url.includes('/werklijsten') && url.includes('/behandeling')) {
    return 'Behandeling wijziging';
  }
  if (url.includes('/werklijsten') && url.includes('/voertuig')) {
    return 'Voertuig wijziging';
  }
  if (url.includes('/werklijsten')) {
    if (method === 'POST') return 'Werklijst aanmaken';
    if (method === 'PUT') return 'Werklijst wijziging';
    if (method === 'DELETE') return 'Werklijst verwijderen';
    return 'Werklijst actie';
  }
  if (url.includes('/klanten')) {
    if (method === 'POST') return 'Klant aanmaken';
    if (method === 'PUT' || method === 'PATCH') return 'Klant wijziging';
    if (method === 'DELETE') return 'Klant verwijderen';
    return 'Klant actie';
  }
  if (url.includes('/instellingen') || url.includes('/settings')) {
    return 'Instellingen opslaan';
  }
  if (url.includes('/werknemers') || url.includes('/users')) {
    if (method === 'POST') return 'Werknemer aanmaken';
    if (method === 'PUT' || method === 'PATCH') return 'Werknemer wijziging';
    if (method === 'DELETE') return 'Werknemer verwijderen';
    return 'Werknemer actie';
  }
  if (url.includes('/voertuigen')) {
    if (method === 'POST') return 'Voertuig aanmaken';
    if (method === 'PUT' || method === 'PATCH') return 'Voertuig wijziging';
    if (method === 'DELETE') return 'Voertuig verwijderen';
    return 'Voertuig actie';
  }
  if (url.includes('/prijzen')) {
    return 'Prijzen opslaan';
  }
  if (url.includes('/handtekening') || url.includes('/signature')) {
    return 'Handtekening opslaan';
  }
  if (url.includes('/certificaat') || url.includes('/haccp')) {
    return 'Certificaat actie';
  }
  if (url.includes('/factuur') || url.includes('/invoice')) {
    return 'Factuur actie';
  }
  if (url.includes('/email') || url.includes('/mail')) {
    return 'E-mail verzenden';
  }
  
  // Fallback
  if (method === 'POST') return 'Aanmaken';
  if (method === 'PUT' || method === 'PATCH') return 'Wijziging opslaan';
  if (method === 'DELETE') return 'Verwijderen';
  return 'Actie opslaan';
}

export default api;
